// src/components/clients/TripView.js
import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Modal, Badge } from '../layout/UI';
import TripMap from './TripMap';
import { getRoute, formatETATime } from '../../services/geocoding';

// Haversine distance in km between two [lat,lng] points
function distanceKm([lat1, lng1], [lat2, lng2]) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function TripView({ truck, shipment, onClose }) {
  const { getHistoryForTruck } = useApp();
  const [plannedRoute, setPlannedRoute] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);

  const hasPlannedRoute = shipment?.originLat && shipment?.destLat;

  // Fetch the planned route line for display
  useEffect(() => {
    let cancelled = false;
    if (hasPlannedRoute) {
      setRouteLoading(true);
      getRoute(
        { lat: shipment.originLat, lng: shipment.originLng },
        { lat: shipment.destLat, lng: shipment.destLng }
      ).then(route => {
        if (!cancelled) {
          setPlannedRoute(route?.geometry || null);
          setRouteLoading(false);
        }
      });
    } else {
      setPlannedRoute(null);
    }
    return () => { cancelled = true; };
  }, [shipment, hasPlannedRoute]);

  // Only this specific shipment's own movement should count as its
  // "actual path traveled" — the truck's getHistoryForTruck() call
  // returns its ENTIRE recorded history with no time boundary, which
  // previously meant older, unrelated deliveries got mixed into the
  // current trip's trail. Scope everything below to history recorded at
  // or after this shipment actually started.
  const tripStart = shipment?.createdAt || shipment?.date || null;

  // Build the actual traveled trail from recorded history (oldest → newest)
  const trail = useMemo(() => {
    if (!truck) return [];
    const events = getHistoryForTruck(truck.id) || [];
    const startMs = tripStart ? new Date(tripStart).getTime() : null;
    return events
      .filter(e => e.lat && e.lng && (startMs == null || new Date(e.timestamp).getTime() >= startMs))
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      .map(e => [e.lat, e.lng]);
  }, [truck, getHistoryForTruck, tripStart]);

  const traveledKm = useMemo(() => {
    if (trail.length < 2) return 0;
    let total = 0;
    for (let i = 1; i < trail.length; i++) total += distanceKm(trail[i - 1], trail[i]);
    return Math.round(total * 10) / 10;
  }, [trail]);

  const idleStopsCount = useMemo(() => {
    if (!truck) return 0;
    const startMs = tripStart ? new Date(tripStart).getTime() : null;
    return (getHistoryForTruck(truck.id) || [])
      .filter(e => e.type === 'idle' && (startMs == null || new Date(e.timestamp).getTime() >= startMs))
      .length;
  }, [truck, getHistoryForTruck, tripStart]);

  if (!truck) return null;

  const currentPosition = truck.location?.lat ? { lat: truck.location.lat, lng: truck.location.lng } : null;
  const progressPct = shipment?.distanceKm ? Math.min(100, Math.round((traveledKm / shipment.distanceKm) * 100)) : null;

  return (
    <Modal open={!!truck} onClose={onClose} title="Trip Trail" width={680}>
      <div>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 10, height: 40, borderRadius: 3, background: truck.color, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 17, color: 'var(--textPrimary)' }}>{truck.plateNumber}</div>
            <div style={{ fontSize: 12, color: 'var(--textMuted)' }}>{shipment?.docNumber} · {shipment?.origin} → {shipment?.destination}</div>
          </div>
          {shipment?.status === 'in-transit' && <Badge color="#3b82f6">In Transit</Badge>}
        </div>

        {/* Map */}
        <TripMap
          origin={hasPlannedRoute ? { lat: shipment.originLat, lng: shipment.originLng } : null}
          destination={hasPlannedRoute ? { lat: shipment.destLat, lng: shipment.destLng } : null}
          plannedRoute={plannedRoute}
          trail={trail}
          currentPosition={currentPosition}
          truckColor={truck.color}
          height={320}
        />

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, marginTop: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--textMuted)' }}>
            <span style={{ width: 16, height: 2, background: '#3b82f6', opacity: 0.6, backgroundImage: 'repeating-linear-gradient(90deg, #3b82f6 0, #3b82f6 4px, transparent 4px, transparent 8px)' }} />
            Planned route
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--textMuted)' }}>
            <span style={{ width: 16, height: 3, background: truck.color, borderRadius: 2 }} />
            Actual path traveled
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--textMuted)' }}>
            🚛 Current location
          </div>
        </div>

        {/* Progress bar */}
        {progressPct != null && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--textMuted)' }}>Trip Progress</span>
              <span style={{ fontSize: 11, color: 'var(--textPrimary)', fontWeight: 600 }}>{progressPct}%</span>
            </div>
            <div style={{ height: 6, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${progressPct}%`, height: '100%', background: truck.color, borderRadius: 4, transition: 'width 0.5s ease' }} />
            </div>
          </div>
        )}

        {/* Stats grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          <div style={{ padding: '10px 12px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)', textAlign: 'center' }}>
            <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Traveled</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--textPrimary)' }}>{traveledKm} km</div>
          </div>
          <div style={{ padding: '10px 12px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)', textAlign: 'center' }}>
            <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Total Distance</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--textPrimary)' }}>{shipment?.distanceKm != null ? `${shipment.distanceKm} km` : '—'}</div>
          </div>
          <div style={{ padding: '10px 12px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)', textAlign: 'center' }}>
            <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>ETA</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#3b82f6' }}>{shipment?.etaTimestamp ? formatETATime(shipment.etaTimestamp) : '—'}</div>
          </div>
          <div style={{ padding: '10px 12px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)', textAlign: 'center' }}>
            <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Idle Stops</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#f59e0b' }}>{idleStopsCount}</div>
          </div>
        </div>

        {routeLoading && (
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--textFaint)', textAlign: 'center' }}>⏳ Loading planned route...</div>
        )}
        {trail.length === 0 && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--textSecondary)' }}>
            No trail recorded yet for this truck — the path will appear here as it moves and reports its position.
          </div>
        )}
      </div>
    </Modal>
  );
}
